import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import {
  usePartnersOfferAction,
  usePartnersReadCampaigns,
  usePartnersReadOffer,
} from '@/api/hooks';
import { type Partner } from '@/api/routes/partners/partners.api';
import Button from '@/components/Button';
import ConfirmModal from '@/components/ConfirmModal';
import LoadErrorState from '@/components/LoadErrorState';
import { IconArchive } from '@/components/icon/tabler/IconArchive';
import { IconArrowLeft } from '@/components/icon/tabler/IconArrowLeft';
import { IconEdit } from '@/components/icon/tabler/IconEdit';
import { IconSend } from '@/components/icon/tabler/IconSend';
import OfferDetailInfo from '@/domain/offers/components/OfferDetailInfo';
import OfferDetailSkeleton from '@/domain/offers/components/OfferDetailSkeleton';
import OfferPreviewCard from '@/domain/offers/components/OfferPreviewCard';
import OfferStatusAlert from '@/domain/offers/components/OfferStatusAlert';
import OfferStatusChip from '@/domain/offers/components/OfferStatusChip';
import { getCampaignByOffer, getPartnerOfferActions } from '@/domain/offers/utils';
import { useTranslate } from '@/state/i18n';
import { ScreenRoutes } from '@/types/router';

interface Props {
  partner: Partner;
}

const CAMPAIGNS_FOR_OFFERS = { offset: 0, limit: 100 };

/** `/offers/:offerId`: the offer as the app shows it, its status and the next step. */
const OfferDetail = ({ partner }: Props) => {
  const t = useTranslate('domain.offers.OfferDetail');
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { offerId = '' } = useParams<{ offerId: string }>();
  const { data: offer, isLoading, isError, refetch } = usePartnersReadOffer(offerId);
  const { data: campaigns } = usePartnersReadCampaigns(CAMPAIGNS_FOR_OFFERS);
  const { mutateAsync: submit, isPending: isSubmitting } = usePartnersOfferAction('submit');
  const { mutateAsync: archive, isPending: isArchiving } = usePartnersOfferAction('archive');
  const [confirmArchive, setConfirmArchive] = useState(false);
  const campaign = useMemo(
    () => getCampaignByOffer(campaigns?.items ?? []).get(offerId),
    [campaigns, offerId],
  );

  if (isLoading) return <OfferDetailSkeleton />;
  if (isError || !offer) {
    return <LoadErrorState message={t('loadError')} onRetry={() => refetch()} />;
  }

  const actions = getPartnerOfferActions(offer, partner.status);

  // Errors (409) are shown by the global snackbar with the API message.
  const handleSubmit = async () => {
    try {
      await submit(offerId);
      enqueueSnackbar(t('submitted'), { variant: 'success' });
    } catch {
      // Already shown.
    }
  };
  const handleArchive = async () => {
    try {
      await archive(offerId);
      enqueueSnackbar(t('archived'), { variant: 'success' });
    } catch {
      // Already shown.
    } finally {
      setConfirmArchive(false);
    }
  };

  return (
    <Stack gap={3}>
      <Stack gap={1.5}>
        <Box>
          <Button
            variant="text"
            size="small"
            startIcon={<IconArrowLeft />}
            onClick={() => navigate(ScreenRoutes.OFFERS)}
          >
            {t('back')}
          </Button>
        </Box>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          gap={2}
        >
          <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
            <Typography component="h1" variant="h2" sx={{ overflowWrap: 'anywhere' }}>
              {offer.title}
            </Typography>
            <OfferStatusChip status={offer.status} />
          </Stack>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {actions.archive ? (
              <Button
                variant="text"
                color="error"
                startIcon={<IconArchive />}
                onClick={() => setConfirmArchive(true)}
              >
                {t('archive')}
              </Button>
            ) : null}
            {actions.edit ? (
              <Button
                variant="outlined"
                startIcon={<IconEdit />}
                onClick={() => navigate(generatePath(ScreenRoutes.OFFER_EDIT, { offerId }))}
              >
                {t('edit')}
              </Button>
            ) : null}
            {actions.submit || actions.submitLocked ? (
              <Button
                variant="contained"
                startIcon={<IconSend />}
                loading={isSubmitting}
                disabled={!actions.submit}
                onClick={handleSubmit}
              >
                {offer.status === 'changes_requested' ? t('resubmit') : t('submit')}
              </Button>
            ) : null}
          </Stack>
        </Stack>
        {actions.submitLocked ? (
          <Typography variant="caption" color="text.secondary">
            {t('submitLocked')}
          </Typography>
        ) : null}
      </Stack>

      <OfferStatusAlert offer={offer} campaign={campaign} />

      <Box sx={sx.columns}>
        <OfferDetailInfo offer={offer} />
        <OfferPreviewCard
          partnerName={partner.displayName}
          title={offer.title}
          body={offer.body}
          imageUrl={offer.imageUrl}
          couponCode={offer.benefit?.code ?? null}
          actions={offer.actions}
          locations={offer.locations}
        />
      </Box>

      <ConfirmModal
        open={confirmArchive}
        title={t('archiveModal.title')}
        message={
          campaign && campaign.status !== 'ended'
            ? t('archiveModal.messageWithCampaign')
            : t('archiveModal.message')
        }
        confirmLabel={t('archiveModal.confirm')}
        cancelLabel={t('archiveModal.back')}
        destructive
        loading={isArchiving}
        onConfirm={handleArchive}
        onClose={() => setConfirmArchive(false)}
      />
    </Stack>
  );
};

export default OfferDetail;

const sx = {
  columns: {
    display: 'grid',
    gap: 3,
    alignItems: 'start',
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 2fr) minmax(0, 1fr)' },
  },
};
